import {
  Collapse,
  DownArrowAlt,
  Expand,
  FastForward,
  LeftArrowAlt,
  RightArrowAlt,
  UpArrowAlt,
} from '@styled-icons/boxicons-regular';
import { Trash } from '@styled-icons/feather';
import { getAnimationClassName } from '@typedream/components';
import type { BlockAnimation as BlockAnimationType, DreamBlock } from '@typedream/core';
import { setNodes, unsetNodes } from '@udecode/plate-core';
import classNames from 'classnames';
import { memo, useState } from 'react';
import { useTPlateEditorRef } from '../../../../plate';
import { getSelectedElementEntry } from '../../../../plugins/elementSelection';
import { useDreamEditorStore } from '../../../../plugins/store';
import { useOutsideEvent } from '../../../../utils/eventAlerter';
import toolbarStyles from '../../SideToolbar.module.css';
import styles from './BlockAnimation.module.css';

type ElementWithAnimation = Extract<DreamBlock, { animation?: any }>;

const displayedAnimations: { name: string; animation: BlockAnimationType }[] = [
  {
    name: 'Fade In',
    animation: {
      type: 'fade_in',
    },
  },
  {
    name: 'Slide In',
    animation: {
      type: 'slide_in',
      direction: 'up',
    },
  },
  {
    name: 'Shrink In',
    animation: {
      type: 'scale_in',
      scaleTo: 'shrink',
    },
  },
  {
    name: 'Grow In',
    animation: {
      type: 'scale_in',
      scaleTo: 'grow',
    },
  },
];

const animationOptions = {
  slide_in: { direction: ['left', 'right', 'up', 'down'], default: 'up' },
  scale_in: { scaleTo: ['grow', 'shrink'], default: 'grow' },
} as const;

function BlockAnimation() {
  const editor = useTPlateEditorRef();

  const [isShowSelectAnimation, setisShowSelectAnimation] = useState(false);

  const currentAnimation = useDreamEditorStore(() => {
    const element = getSelectedElementEntry<ElementWithAnimation>(editor)?.[0];
    return element?.animation;
  });

  const menuRef = useOutsideEvent<HTMLDivElement>('mousedown', () => {
    setisShowSelectAnimation(false);
  });

  const handleSelectAnimation = (animation: BlockAnimationType) => {
    const path = getSelectedElementEntry<ElementWithAnimation>(editor)?.[1];
    if (path) {
      setNodes(editor, { animation }, { at: path });
    }
  };

  const handleRemoveAnimation = () => {
    const path = getSelectedElementEntry<ElementWithAnimation>(editor)?.[1];
    if (path) {
      unsetNodes<ElementWithAnimation>(editor, ['animation'], { at: path });
    }
  };

  const renderSelectAnimation = () => (
    <div className={classNames(toolbarStyles.grid, styles.floating)}>
      {displayedAnimations.map((item) => (
        <div
          key={item.name}
          className="is-flex is-flex-direction-column is-align-items-center is-justify-content-center"
          onClick={() => {
            handleSelectAnimation(item.animation);
            setisShowSelectAnimation(false);
          }}
          role="none"
        >
          <div className={styles.animationContainer}>
            <div
              className={classNames(
                styles.animationItem,
                styles[getAnimationClassName(item.animation) || ''],
              )}
            />
          </div>
          <div className="is-size-7" style={{ color: 'var(--text-lighter)' }}>
            {item.name}
          </div>
        </div>
      ))}
    </div>
  );

  const renderAnimationOptions = () => (
    <div className={toolbarStyles.card}>
      <div
        className={classNames(
          'is-flex is-align-items-center is-justify-content-space-between',
        )}
      >
        <div className={styles.currentAnimation}>
          <FastForward size={20} />
          {currentAnimation?.type && currentAnimation?.type?.replace('_', ' ')}
        </div>
        <div className="is-flex is-align-items-center">
          <button
            className={toolbarStyles.icon}
            type="button"
            onClick={handleRemoveAnimation}
          >
            <Trash size={16} />
          </button>
        </div>
      </div>

      {/* SLIDE IN OPTIONS */}
      {currentAnimation?.type === 'slide_in' && (
        <div className="mt-2">
          <label className={toolbarStyles.subLabel} htmlFor="#">
            Direction
          </label>
          <div className={toolbarStyles.menuGroup}>
            {animationOptions.slide_in.direction.map((direction) => (
              <button
                key={direction}
                className={classNames(toolbarStyles.menuItem, {
                  [toolbarStyles.active]:
                    currentAnimation
                    && 'direction' in currentAnimation
                    && currentAnimation.direction === direction,
                })}
                type="button"
                onClick={() => {
                  handleSelectAnimation({
                    type: 'slide_in',
                    direction,
                  });
                }}
              >
                <div className="p-1">
                  {direction === 'left' && <LeftArrowAlt size={18} />}
                  {direction === 'right' && <RightArrowAlt size={18} />}
                  {direction === 'up' && <UpArrowAlt size={18} />}
                  {direction === 'down' && <DownArrowAlt size={18} />}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* SCALE IN OPTIONS */}
      {currentAnimation?.type === 'scale_in' && (
        <div className="mt-2">
          <label className={toolbarStyles.subLabel} htmlFor="#">
            Behavior
          </label>
          <div className={toolbarStyles.menuGroup}>
            {animationOptions.scale_in.scaleTo.map(
              (scaleTo: 'grow' | 'shrink') => (
                <button
                  key={scaleTo}
                  className={classNames(toolbarStyles.menuItem, {
                    [toolbarStyles.active]:
                      currentAnimation
                      && 'scaleTo' in currentAnimation
                      && currentAnimation.scaleTo === scaleTo,
                  })}
                  type="button"
                  onClick={() => {
                    handleSelectAnimation({
                      type: 'scale_in',
                      scaleTo,
                    });
                  }}
                >
                  <div className="p-1">
                    {scaleTo === 'grow' && <Expand size={18} />}
                    {scaleTo === 'shrink' && <Collapse size={18} />}
                  </div>
                </button>
              ),
            )}
          </div>
        </div>
      )}

      {/* TRIGGER, NO OPTIONS CURRENTLY */}
      <div className="mt-2">
        <label className={toolbarStyles.subLabel} htmlFor="#">
          Trigger
        </label>
        <div style={{ color: 'var(--text)' }}>On Scroll</div>
      </div>
    </div>
  );

  return (
    <div className={toolbarStyles.section}>
      <label htmlFor="#" className={toolbarStyles.label}>
        Animation
      </label>
      <div ref={menuRef} className={styles.menu}>
        {currentAnimation ? (
          renderAnimationOptions()
        ) : (
          <button
            type="button"
            className={classNames(toolbarStyles.button)}
            onClick={() => setisShowSelectAnimation((prev) => !prev)}
          >
            Add Animation
          </button>
        )}
        {isShowSelectAnimation && renderSelectAnimation()}
      </div>
    </div>
  );
}

export default memo(BlockAnimation);
